'use client';

import { CSSProperties, FC } from 'react';
import classNames from 'classnames';
import { Container, PrismicImageWithAlt } from '@/components';
import { getSlicePadding } from '@/utils';
import { BannerSlice, BannerSliceWithLogoOnBorder } from '@/types';
import { BannerImages, getBannerSectionStyle } from './Banner.components';
import styles from './banner.module.scss';

const DEFAULT_SIDE_IMAGE_DIMENSION = 76;

const WithLogoOnBorderVariation: FC<BannerSlice & BannerSliceWithLogoOnBorder> =
  slice => {
    const banner = slice.items[0];
    const primary = slice?.primary;
    const bannerActive = Boolean(banner?.active);
    const showMissingBannerPlaceholder = false;

    if (!slice || !primary?.active) {
      return null;
    }

    const {
      background_color,
      bottom_spacing,
      adjust_aspect_ratio_desktop,
      adjust_aspect_ratio_mobile,
      max_height,
      max_height_mobile,
    } = primary;

    const sideImageHeight =
      banner?.side_image_height ?? DEFAULT_SIDE_IMAGE_DIMENSION;
    const extraSpacingValue = sideImageHeight / 3;

    return (
      <section
        className={classNames(
          styles.page_banner__section,
          getSlicePadding(slice?.primary?.padding_section),
          {
            [styles.page_banner__section__with_side_image_spacing_bottom]:
              bottom_spacing,
            [styles.page_banner__section__extra_spacing]: !bottom_spacing,
            [styles.page_banner__section__side_image_center_mobile]:
              banner?.center_side_image_on_mobile,
          },
        )}
        style={
          {
            ...getBannerSectionStyle(primary),
            '--extra-spacing': `${extraSpacingValue}px`,
            '--mobile-extra-spacing': `${DEFAULT_SIDE_IMAGE_DIMENSION / 3}px`,
          } as CSSProperties
        }
      >
        <Container className={styles.page_banner__container}>
          <BannerImages
            adjustAspectRatioDesktop={adjust_aspect_ratio_desktop}
            adjustAspectRatioMobile={adjust_aspect_ratio_mobile}
            banner={banner}
            bannerActive={bannerActive}
            maxHeightDesktop={max_height}
            maxHeightMobile={max_height_mobile}
            primaryBackgroundColor={background_color}
            showMissingBannerPlaceholder={showMissingBannerPlaceholder}
          />

          {banner?.side_image?.url && (
            <div
              className={classNames(styles.page__aside_image, {
                [styles.page__aside_image__bottom_start]:
                  banner.horizontal_position === 'start',
                [styles.page__aside_image__bottom_end]:
                  banner.horizontal_position === 'end',
                [styles.page__aside_image__bottom_center]:
                  banner.horizontal_position === 'center',
                [styles.page__aside_image__bottom_center_mobile]:
                  banner.center_side_image_on_mobile,
              })}
              style={
                {
                  width:
                    banner.side_image_width ?? DEFAULT_SIDE_IMAGE_DIMENSION,
                  height:
                    banner.side_image_height ?? DEFAULT_SIDE_IMAGE_DIMENSION,
                  borderRadius: banner.side_image_border_radius ?? '100px',
                  '--border-dimension': `${banner.side_image_border_dimension}px`,
                  '--border-color': banner.side_image_border_color,
                } as CSSProperties
              }
            >
              <PrismicImageWithAlt
                defaultAlt="Banner"
                field={banner.side_image}
                fill
                style={{
                  objectFit: 'cover',
                  objectPosition: 'center',
                }}
              />
            </div>
          )}
        </Container>
      </section>
    );
  };

export default WithLogoOnBorderVariation;
